import { useState, useEffect } from 'react';
import axios from 'axios';

interface ToolRequest {
  id: string;
  timestamp: string;
  capability: string;
  reason: string;
  suggested_implementation: string;
  priority: 'low' | 'medium' | 'high';
  status: 'pending' | 'in-progress' | 'done' | 'rejected';
  source?: string;
}

export default function ToolRequests() {
  const [requests, setRequests] = useState<ToolRequest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/api/tool-requests')
      .then(r => setRequests(Array.isArray(r.data) ? r.data : []))
      .catch((err) => { 
        console.error('Failed to fetch tool requests:', err); 
        setRequests([]); 
      })
      .finally(() => setLoading(false));
  }, []);

  const updateStatus = async (id: string, status: string) => {
    try {
      await axios.patch('/api/tool-requests', { id, status });
      setRequests(prev => prev.map(r => r.id === id ? { ...r, status: status as any } : r));
    } catch (err) {
      console.error('Failed to update tool request status:', err);
    }
  };

  const priorityColor: Record<string, string> = {
    high: '#f87171',
    medium: '#fbbf24',
    low: '#6b7280',
  };

  const statusColor: Record<string, string> = {
    pending: '#fbbf24',
    'in-progress': '#6366f1',
    done: '#4ade80',
    rejected: '#6b7280',
  };

  const pending = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="tool-requests-panel">
      <div className="tr-header">
        <h2>Tool Requests</h2>
        <p className="tr-subtitle">
          Capability requests flagged during market evaluation
          {pending > 0 && <span className="tr-pending-badge">{pending} pending</span>}
        </p>
      </div>
      {loading && <div className="tr-empty">Loading…</div>}
      {!loading && requests.length === 0 && (
        <div className="tr-empty">No tool requests at this time.</div>
      )}
      {requests.map(r => (
        <div key={r.id} className="tr-card" style={{ borderLeft: `3px solid ${priorityColor[r.priority] ?? '#444'}` }}>
          <div className="tr-card-header">
            <span className="tr-capability" style={{ fontWeight: 'bold' }}>{r.capability}</span>
            <span className="tr-priority" style={{ color: priorityColor[r.priority] ?? '#aaa', marginLeft: 8 }}>{r.priority}</span>
            <span className="tr-status" style={{ color: statusColor[r.status] ?? '#aaa', marginLeft: 8 }}>{r.status}</span>
            <span className="tr-time" style={{ marginLeft: 'auto', fontSize: '0.8rem', color: '#888' }}>{new Date(r.timestamp).toLocaleString()}</span>
          </div>
          {r.reason && <div className="tr-reason" style={{ marginTop: 4, color: '#ccc' }}>{r.reason}</div>}
          {r.suggested_implementation && (
            <div className="tr-impl" style={{ marginTop: 4, fontSize: '0.85rem', color: '#aaa' }}>
              <strong>Suggested:</strong> {r.suggested_implementation}
            </div>
          )}
          {r.source && <div className="tr-source" style={{ marginTop: 4, fontSize: '0.8rem', color: '#666' }}>Source: {r.source}</div>}
          {r.status === 'pending' && (
            <div className="tr-actions" style={{ marginTop: 8, display: 'flex', gap: 6 }}>
              <button onClick={() => updateStatus(r.id, 'in-progress')} style={{ padding: '3px 10px', background: '#6366f1', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Start</button>
              <button onClick={() => updateStatus(r.id, 'done')} style={{ padding: '3px 10px', background: '#4ade80', color: 'black', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Done</button>
              <button onClick={() => updateStatus(r.id, 'rejected')} style={{ padding: '3px 10px', background: '#374151', color: '#9ca3af', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Reject</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
